import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { BreadcrumbHeader, StatusBadge, EmptyState, ActionMenu, ConfirmModal, EditSerialModal } from '../../components/shared';
import { warehouses, stockLevels, parts, serialItems } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Constants ────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const REGION_OPTIONS = [
'North America - East',
'North America - West',
'North America - Central',
'Europe - West',
'Europe - East',
'Asia Pacific',
'Middle East & Africa',
'Latin America'];

// ─── Table style constants ────────────────────────────────────────────────────
const TH = {
  backgroundColor: 'var(--secondary-light)',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--grey-600)',
  textTransform: 'none',
  letterSpacing: 'normal',
  px: 2,
  py: '10px',
  borderBottom: '1px solid var(--grey-200)',
  whiteSpace: 'nowrap'
};

const TD = {
  px: 2,
  py: 0,
  border: 'none',
  fontSize: '0.875rem',
  color: 'var(--grey-800)',
  height: 44
};

// Modal sub-table header style
const MTH = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--grey-500)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  px: 0,
  py: '6px',
  border: 'none',
  backgroundColor: 'transparent'
};

const MTD = {
  fontSize: '0.8125rem',
  color: 'var(--grey-800)',
  px: 0,
  py: 0,
  border: 'none',
  height: 36,
  borderBottom: '1px solid var(--grey-200)'
};

// ─── Part detail modal ─────────────────────────────────────────────────────────
function PartDetailModal({ entry, locationName, localSerials, onClose, onAddSerial, onEditSerial, onDeleteSerial, navigate }) {
  if (!entry) return null;

  const partSerials = localSerials.filter((s) => s.partNumber === entry.partNumber);
  const availableQty = partSerials.length > 0 ?
  partSerials.filter((s) => s.status === 'Available').length :
  entry.quantityOnHand;
  const isLow = availableQty < entry.reorderThreshold;
  const isBulk = partSerials.length === 0 && entry.quantityOnHand > 0;

  return (
    <Dialog
      open={Boolean(entry)}
      onClose={(_, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="sm"
      fullWidth>
      
      <DialogTitle sx={{ fontWeight: 600,
        fontSize: '1rem',
        color: 'var(--grey-900)',
        pb: 1,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography
            onClick={() => entry.partId && navigate(`/inventory/search/${entry.partId}`)}
            sx={{ fontWeight: 700,
              fontSize: '1rem',
              color: entry.partId ? '#009688' : 'var(--grey-900)',
              cursor: entry.partId ? 'pointer' : 'default',
              '&:hover': entry.partId ? { textDecoration: 'underline' } : {}
            }}>
            
            {entry.partNumber}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px', fontWeight: 400 }}>
            {entry.description}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pb: 3, pt: '12px !important' }}>
        {/* Stats row */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2.5 }}>
          {[
          { label: 'Available', value: availableQty, alert: isLow },
          { label: 'On Hand', value: entry.quantityOnHand },
          { label: 'Threshold', value: entry.reorderThreshold },
          { label: 'Last Updated', value: entry.lastUpdated, wide: true }]
          .map(({ label, value, alert, wide }) =>
          <Box key={label} sx={{ flex: wide ? 2 : 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', mb: '2px' }}>{label}</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: alert ? '#C62828' : 'var(--grey-900)', lineHeight: 1.2, wordBreak: 'break-word' }}>
                {value ?? '—'}
              </Typography>
            </Box>
          )}
        </Box>

        {isLow &&
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#FFF3E0', border: '1px solid #FFB300', borderRadius: '6px', px: 1.5, py: 1, mb: 2 }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: '#E65100', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8125rem', color: '#BF360C' }}>
              Stock is below the minimum threshold of {entry.reorderThreshold}.
            </Typography>
          </Box>
        }

        {/* Serial section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-600)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Serial Items
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: '13px !important' }} />}
            onClick={() => onAddSerial(entry.partNumber)}
            sx={{ fontSize: '0.8125rem', textTransform: 'none', color: 'var(--secondary-main)',
              p: '2px 8px', minWidth: 0,
              visibility: isBulk ? 'hidden' : 'visible'
            }}>
            
            Add Serial
          </Button>
        </Box>

        {isBulk ?
        <Box sx={{ backgroundColor: 'var(--grey-50)', border: '1px solid var(--grey-200)', borderRadius: '8px', px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: '4px' }}>Bulk Inventory</Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
              {entry.quantityOnHand} unit{entry.quantityOnHand !== 1 ? 's' : ''} on hand — not individually serialized at this location.
            </Typography>
          </Box> :
        partSerials.length === 0 ?
        <Box sx={{ backgroundColor: 'var(--grey-50)', border: '1px solid var(--grey-200)', borderRadius: '8px', px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic' }}>
              No serial numbers recorded for this part.
            </Typography>
          </Box> :

        <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...MTH, px: 2, width: '40%' }}>Serial #</TableCell>
                  <TableCell sx={{ ...MTH, px: 2, width: '22%' }}>Status</TableCell>
                  <TableCell sx={{ ...MTH, px: 2, width: '25%' }}>Ticket #</TableCell>
                  <TableCell sx={{ ...MTH, px: 2, width: '13%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {partSerials.map((s, i) =>
              <TableRow key={s.id} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                    <TableCell sx={{ ...MTD, px: 2, fontFamily: 'monospace', letterSpacing: '0.02em' }}>
                      {s.serialNumber}
                    </TableCell>
                    <TableCell sx={{ ...MTD, px: 2 }}>
                      <StatusBadge status={s.status} />
                    </TableCell>
                    <TableCell sx={{ ...MTD, px: 2, color: s.ticketNumber ? 'var(--grey-700)' : 'var(--grey-400)' }}>
                      {s.ticketNumber ?? '—'}
                    </TableCell>
                    <TableCell sx={{ ...MTD, px: 2, textAlign: 'right' }}>
                      <ActionMenu actions={[
                  { label: 'Edit', onClick: () => onEditSerial(entry.partNumber, s) },
                  { label: 'Delete', onClick: () => onDeleteSerial(s.id) }]
                  } />
                    </TableCell>
                  </TableRow>
              )}
              </TableBody>
            </Table>
          </Box>
        }
      </DialogContent>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function StockLocationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stockSearch, setStockSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [banner, setBanner] = useState('');
  const [localOverride, setLocalOverride] = useState(null);
  const [localEntries, setLocalEntries] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null); // entry object | null
  const [serialModal, setSerialModal] = useState(null); // { mode, partNumber, serial? } | null
  const [deleteTarget, setDeleteTarget] = useState(null); // serial id | null

  const baseLocation = warehouses.find((w) => w.id === id);
  const location = localOverride ?? baseLocation;

  const [localSerials, setLocalSerials] = useState(
    () => location ? serialItems.filter((si) => si.locationName === location.name) : []
  );

  const refreshSerials = () =>
  setLocalSerials(serialItems.filter((si) => si.locationName === location.name));

  const refreshEntries = () => {
    stockLevels
    .filter((sl) => sl.locationName === location.name)
    .forEach((sl) => {
      sl.quantityOnHand = serialItems.filter(
        (si) => si.partNumber === sl.partNumber &&
        si.locationName === location.name &&
        si.status === 'Available'
      ).length;
    });
    const refreshed = stockLevels
    .filter((sl) => sl.locationName === location.name)
    .map((sl) => {
      const partRec = parts.find((p) => p.partNumber === sl.partNumber);
      return {
        ...sl,
        description: partRec?.description ?? '—',
        category: partRec?.category ?? '—',
        reorderThreshold: partRec?.reorderThreshold ?? 0,
        partId: partRec?.id ?? null
      };
    });
    setLocalEntries(refreshed);
  };

  const handleSerialSave = (values) => {
    if (serialModal.mode === 'add') {
      serialItems.unshift({
        id: `f3a4b5c6-0013-4000-8013-${Date.now()}`,
        partNumber: serialModal.partNumber,
        ...values
      });
    } else {
      const s = serialItems.find((si) => si.id === serialModal.serial.id);
      if (s) Object.assign(s, values);
    }
    refreshSerials();
    refreshEntries();
    setSerialModal(null);
  };

  const handleSerialDelete = () => {
    const idx = serialItems.findIndex((si) => si.id === deleteTarget);
    if (idx !== -1) serialItems.splice(idx, 1);
    refreshSerials();
    refreshEntries();
    setDeleteTarget(null);
  };

  const derivedEntries = useMemo(() => {
    if (!location) return [];
    return stockLevels
    .filter((sl) => sl.locationName === location.name)
    .map((sl) => {
      const partRec = parts.find((p) => p.partNumber === sl.partNumber);
      return {
        ...sl,
        description: partRec?.description ?? '—',
        category: partRec?.category ?? '—',
        reorderThreshold: partRec?.reorderThreshold ?? 0,
        partId: partRec?.id ?? null
      };
    });
  }, [location]);

  const stockEntries = localEntries ?? derivedEntries;

  const filteredEntries = useMemo(() => {
    const q = stockSearch.trim().toLowerCase();
    if (!q) return stockEntries;
    return stockEntries.filter(
      (e) =>
      e.partNumber.toLowerCase().includes(q) ||
      e.description.toLowerCase().includes(q)
    );
  }, [stockEntries, stockSearch]);

  const openEdit = () => {
    if (!location) return;
    setEditForm({
      name: location.name,
      type: location.type ?? 'Warehouse',
      region: location.region ?? '',
      address: location.address ?? '',
      city: location.city ?? '',
      state: location.state ?? '',
      zip: location.zip ?? '',
      contactName: location.contactName ?? '',
      contactEmail: location.contactEmail ?? '',
      contactPhone: location.contactPhone ?? '',
      notes: location.notes ?? '',
      active: location.active
    });
    setEditOpen(true);
  };

  const handleEditSave = () => {
    const updated = { ...location, ...editForm };
    const idx = warehouses.findIndex((w) => w.id === id);
    if (idx !== -1) Object.assign(warehouses[idx], updated);
    setLocalOverride(updated);
    setEditOpen(false);
    setBanner('Location updated successfully.');
    setTimeout(() => setBanner(''), 4000);
  };

  const setField = (field) => (e) =>
  setEditForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isFormValid =
  editForm?.name &&
  editForm?.address &&
  editForm?.city &&
  editForm?.state &&
  editForm?.zip &&
  editForm?.contactName &&
  editForm?.contactEmail;

  if (!location) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/inventory/stock-locations')}
          sx={{ textTransform: 'none', color: 'var(--grey-600)', mb: 2, fontSize: '0.875rem' }}>
          
          Back to Stock Locations
        </Button>
        <EmptyState title="Location not found" description="This stock location does not exist or has been removed." />
      </Box>);

  }

  const infoFields = [
  { label: 'Location Code', value: location.code },
  { label: 'Type', value: <StatusBadge status={location.type} /> },
  { label: 'Region', value: location.region ?? '—' },
  ...(location.type === 'On-Site' ?
  [{ label: 'Customer', value: location.customerName ?? '—' }] :
  []),
  { label: 'Address', value: `${location.address}, ${location.city}, ${location.state}` },
  { label: 'Contact Name', value: location.contactName },
  { label: 'Contact Email', value:
    <Typography
      component="a"
      href={`mailto:${location.contactEmail}`}
      sx={{ fontSize: '0.875rem', color: '#009688', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      
          {location.contactEmail}
        </Typography>

  },
  { label: 'Status', value: <StatusBadge status={location.active ? 'Active' : 'Inactive'} /> }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {banner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {banner}
          </Typography>
        </Box>
      }

      {/* Page header */}
      <BreadcrumbHeader
        title={location.name}
        subtitle={location.type}
        crumbs={[
        { label: 'Inventory' },
        { label: 'Stock Locations', path: '/inventory/stock-locations' },
        { label: location.name }]
        } />

      {/* Part detail modal */}
      <PartDetailModal
        entry={selectedEntry}
        locationName={location.name}
        localSerials={localSerials}
        navigate={navigate}
        onClose={() => setSelectedEntry(null)}
        onAddSerial={(partNumber) => {
          setSerialModal({ mode: 'add', partNumber, serial: { locationName: location.name } });
        }}
        onEditSerial={(partNumber, serial) => {
          setSerialModal({ mode: 'edit', partNumber, serial });
        }}
        onDeleteSerial={(serialId) => setDeleteTarget(serialId)} />

      {/* Two-column layout — Stock Inventory left, Location Info right */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

        {/* Left — Stock Inventory */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ContentCard
            sx={{ p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
            
            {/* Panel header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', whiteSpace: 'nowrap' }}>
                Stock Inventory ({stockEntries.length})
              </Typography>
              <TextField
                size="small"
                placeholder="Search parts…"
                value={stockSearch}
                onChange={(e) => setStockSearch(e.target.value)}
                InputProps={{
                  startAdornment:
                  <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'var(--grey-400)' }} />
                    </InputAdornment>

                }}
                sx={{ width: 200,
                  '& .MuiInputBase-root': { fontSize: '0.8125rem', height: 32 },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'var(--grey-200)' },
                    '&.Mui-focused fieldset': { borderColor: '#009688' }
                  }
                }} />
              
            </Box>

            <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
              {stockEntries.length === 0 ?
              <Box sx={{ px: 3, py: 3 }}>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic' }}>
                    No stock on hand at this location.
                  </Typography>
                </Box> :

              <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Part #', 'Description', 'Category', 'Available', 'On Hand', 'Threshold', 'Last Updated'].map((h) =>
                    <TableCell key={h} sx={{ ...TH, py: '8px' }}>{h}</TableCell>
                    )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEntries.length === 0 ?
                  <TableRow>
                        <TableCell colSpan={7} sx={{ ...TD, textAlign: 'center', color: 'var(--grey-400)', fontStyle: 'italic' }}>
                          No parts match your search.
                        </TableCell>
                      </TableRow> :

                  filteredEntries.map((entry) => {
                    const partSerials = localSerials.filter((s) => s.partNumber === entry.partNumber);
                    const availableQty = partSerials.length > 0 ?
                    partSerials.filter((s) => s.status === 'Available').length :
                    entry.quantityOnHand;
                    const isLow = availableQty < entry.reorderThreshold;

                    return (
                      <TableRow
                        key={entry.id}
                        hover
                        onClick={() => setSelectedEntry(entry)}
                        sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 'none' } }}>
                        
                            <TableCell sx={TD}>
                              <Typography sx={{ fontSize: '0.875rem', color: '#009688', fontWeight: 500 }}>
                                {entry.partNumber}
                              </Typography>
                            </TableCell>
                            <TableCell sx={TD}>{entry.description}</TableCell>
                            <TableCell sx={TD}>{entry.category}</TableCell>
                            <TableCell sx={TD}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: isLow ? '#C62828' : 'var(--grey-900)' }}>
                                  {availableQty}
                                </Typography>
                                {isLow && <WarningAmberIcon sx={{ fontSize: 14, color: '#E65100' }} />}
                              </Box>
                            </TableCell>
                            <TableCell sx={TD}>{entry.quantityOnHand}</TableCell>
                            <TableCell sx={{ ...TD, color: 'var(--grey-500)' }}>{entry.reorderThreshold}</TableCell>
                            <TableCell sx={{ ...TD, color: 'var(--grey-600)' }}>{entry.lastUpdated}</TableCell>
                          </TableRow>);

                  })
                  }
                  </TableBody>
                </Table>
              }
            </Box>
          </ContentCard>
        </Box>

        {/* Right — Location Info */}
        <Box sx={{ width: 340, flexShrink: 0 }}>
          <ContentCard
            sx={{ overflow: 'hidden'
            }}>
            
            {/* Card header */}
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid var(--grey-200)' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
                Location Info
              </Typography>
            </Box>

            {/* Fields */}
            <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {infoFields.map((field) =>
              <Box key={field.label} sx={{ display: 'flex', gap: 2 }}>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 100, flexShrink: 0 }}>
                    {field.label}
                  </Typography>
                  <Typography component="div" sx={{ fontSize: '0.875rem', color: 'var(--grey-900)', flex: 1 }}>
                    {field.value ?? '—'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Edit button */}
            <Box sx={{ px: 3, py: 2, borderTop: '1px solid var(--grey-200)' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={openEdit}
                fullWidth
                sx={{ textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderColor: 'var(--grey-300)',
                  color: 'var(--grey-700)',
                  '&:hover': { borderColor: 'var(--grey-400)', backgroundColor: 'var(--grey-50)' },
                  boxShadow: 'none'
                }}>
                
                Edit Location
              </Button>
            </Box>
          </ContentCard>
        </Box>
      </Box>

      {/* Serial Edit / Add modal */}
      {serialModal &&
      <EditSerialModal
        open
        mode={serialModal.mode}
        partNumber={serialModal.partNumber}
        serial={serialModal.serial}
        onClose={() => setSerialModal(null)}
        onSave={handleSerialSave} />

      }

      {/* Delete confirmation */}
      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Delete Serial Number"
        message="Are you sure you want to remove this serial number? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleSerialDelete}
        onCancel={() => setDeleteTarget(null)} />

      {/* Edit Location modal */}
      {editForm &&
      <Dialog
        open={editOpen}
        onClose={(_, reason) => {if (reason !== 'backdropClick') setEditOpen(false);}}
        maxWidth="sm"
        fullWidth>
        
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Edit Location
            <IconButton size="small" onClick={() => setEditOpen(false)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
              <HighlightOffIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
            <TextField
            size="small" label="Name" value={editForm.name} onChange={setField('name')}
            fullWidth required sx={fieldSx} />

            {/* Location type */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', minWidth: 36 }}>Type</Typography>
              <ToggleButtonGroup
              exclusive
              size="small"
              value={editForm.type}
              onChange={(_, val) => {if (val) setEditForm((prev) => ({ ...prev, type: val }));}}
              sx={{ '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  px: 2,
                  py: '4px',
                  borderColor: 'var(--grey-200)',
                  color: 'var(--grey-500)',
                  '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                  '&.Mui-selected:hover': { backgroundColor: '#00796B' }
                }
              }}>
              
                <ToggleButton value="Warehouse">Warehouse</ToggleButton>
                <ToggleButton value="FSL">FSL</ToggleButton>
                <ToggleButton value="On-Site">On-Site</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel>Region</InputLabel>
              <Select
              label="Region"
              value={editForm.region ?? ''}
              onChange={(e) => setEditForm((prev) => ({ ...prev, region: e.target.value }))}
              sx={{ fontSize: '0.875rem' }}>
              
                {REGION_OPTIONS.map((r) =>
              <MenuItem key={r} value={r} sx={{ fontSize: '0.875rem' }}>{r}</MenuItem>
              )}
              </Select>
            </FormControl>

            <TextField
            size="small" label="Address" value={editForm.address} onChange={setField('address')}
            fullWidth required sx={fieldSx} />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
              size="small" label="City" value={editForm.city} onChange={setField('city')}
              fullWidth required sx={fieldSx} />
            
              <TextField
              size="small" label="State" value={editForm.state} onChange={setField('state')}
              sx={{ width: 100, ...fieldSx }} required />
            
              <TextField
              size="small" label="Zip Code" value={editForm.zip} onChange={setField('zip')}
              sx={{ width: 120, ...fieldSx }} required />
            
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
              size="small" label="Contact Name" value={editForm.contactName} onChange={setField('contactName')}
              fullWidth required sx={fieldSx} />
            
              <TextField
              size="small" label="Contact Email" type="email" value={editForm.contactEmail} onChange={setField('contactEmail')}
              fullWidth required sx={fieldSx} />
            
            </Box>

            <TextField
            size="small" label="Contact Phone (optional)" value={editForm.contactPhone} onChange={setField('contactPhone')}
            fullWidth sx={fieldSx} />

            <TextField
            size="small" label="Notes (optional)" value={editForm.notes} onChange={setField('notes')}
            multiline rows={2} fullWidth sx={fieldSx} />

            {/* Status toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>Status</Typography>
              <ToggleButtonGroup
              exclusive
              size="small"
              value={editForm.active ? 'active' : 'inactive'}
              onChange={(_, val) => {if (val) setEditForm((prev) => ({ ...prev, active: val === 'active' }));}}
              sx={{ '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  px: 2,
                  py: '4px',
                  borderColor: 'var(--grey-200)',
                  color: 'var(--grey-500)',
                  '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                  '&.Mui-selected:hover': { backgroundColor: '#00796B' }
                }
              }}>
              
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="inactive">Inactive</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Typography
            onClick={() => setEditOpen(false)}
            sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
            
              Cancel
            </Typography>
            <Button
            variant="contained"
            size="small"
            disabled={!isFormValid}
            onClick={handleEditSave}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none',
              px: 2
            }}>
            
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      }
    </Box>);

}